// apps/landing-page/src/lib/chunk-reload.ts

// A deploy replaces the hashed files under /_next/static and deletes the old ones, so a
// tab that was opened before the deploy asks for a chunk that no longer exists the moment
// it navigates. The user sees the error page for a build that is simply gone; reloading
// fetches fresh HTML pointing at chunks that do exist.
const RELOAD_FLAG = 'aks_chunk_reloaded';

export function isChunkLoadError(error: Error & { digest?: string }): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    /loading chunk .* failed|failed to load chunk/i.test(error.message)
  );
}

/**
 * Reloads once when a stale-chunk error is what broke the page. Returns true if a reload
 * was triggered, so the caller can keep the error UI hidden while the page navigates.
 *
 * The once-per-session flag is the whole point: if the reload does NOT fix it (CDN serving
 * a bad build, an offline device), a naive reload-on-error boundary becomes an infinite
 * refresh loop that is far worse than the error page. When sessionStorage is unavailable
 * this deliberately fails closed and shows the error page rather than reload unguarded —
 * reading the property itself can throw when site data is blocked.
 */
export function recoverFromChunkError(error: Error & { digest?: string }): boolean {
  if (!isChunkLoadError(error)) return false;

  try {
    if (window.sessionStorage.getItem(RELOAD_FLAG)) return false;
    window.sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    return false;
  }

  window.location.reload();
  return true;
}
